<script setup>
import { formatMoney } from "../../utils/analyticsUtils.js";

defineProps({
  kpis: {
    type: Object,
    default: () => ({
      ventas: 0,
      ingresos: 0,
      ingresosVar: 0,
      ventasVar: 0,
      compras: 0,
      comprasImporte: 0,
      comprasVar: 0,
      impuestos: 0,
    }),
  },
  clientsCount: { type: Number, default: 0 },
  ordersCount: { type: Number, default: 0 },
  periodo: { type: String, default: "30d" },
  animatedClients: { type: Number, default: 0 },
  animatedIncome: { type: Number, default: 0 },
  animatedOrders: { type: Number, default: 0 },
});

defineEmits(["update:periodo"]);

function clampPercent(v) {
  const n = Number(v || 0);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.min(100, n);
}
</script>

<template>
  <header class="hero-balance">
    <div class="hero-left">
      <span class="hero-label">Balance total</span>
      <div class="hero-value">
        {{ formatMoney(kpis.ingresos - kpis.comprasImporte) }}
      </div>
      <div
        class="hero-var"
        :class="kpis.ingresosVar - kpis.comprasVar >= 0 ? 'up' : 'down'"
      >
        {{ clampPercent(kpis.ingresosVar - kpis.comprasVar).toFixed(1) }}%
      </div>
    </div>
    <div class="head-tools">
      <button
        class="chip"
        :class="{ active: periodo === 'hoy' }"
        @click="$emit('update:periodo', 'hoy')"
      >
        Hoy
      </button>
      <button
        class="chip"
        :class="{ active: periodo === '7d' }"
        @click="$emit('update:periodo', '7d')"
      >
        7 días
      </button>
      <button
        class="chip"
        :class="{ active: periodo === '30d' }"
        @click="$emit('update:periodo', '30d')"
      >
        30 días
      </button>
      <button
        class="chip"
        :class="{ active: periodo === 'mes' }"
        @click="$emit('update:periodo', 'mes')"
      >
        Este mes
      </button>
    </div>
  </header>

  <section class="kpis-cards">
    <div class="soft-card">
      <div class="card-title">Clientes</div>
      <div class="card-value">{{ Math.round(animatedClients || clientsCount) }}</div>
      <div class="card-sub">Comparado con período anterior</div>
    </div>
    <div class="soft-card">
      <div class="card-title">Ingresos</div>
      <div class="card-value">{{ formatMoney(animatedIncome || kpis.ingresos) }}</div>
      <div class="card-badge" :class="kpis.ingresosVar >= 0 ? 'up' : 'down'">
        {{ clampPercent(kpis.ingresosVar).toFixed(1) }}%
      </div>
    </div>
    <div class="soft-card">
      <div class="card-title">Pedidos</div>
      <div class="card-value">{{ Math.round(animatedOrders || ordersCount) }}</div>
      <div class="card-badge" :class="kpis.ventasVar >= 0 ? 'up' : 'down'">
        {{ clampPercent(kpis.ventasVar).toFixed(1) }}%
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}
.hero-label {
  font-weight: 600;
  opacity: 0.9;
}
.hero-value {
  font-size: 2rem;
  font-weight: 900;
}
.hero-var {
  font-weight: 700;
  margin-top: 0.15rem;
}
.hero-var.up {
  color: #9ef0a1;
}
.hero-var.down {
  color: #fecaca;
}
.head-tools {
  display: inline-flex;
  gap: 0.5rem;
}
.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
}
.chip.active {
  background: var(--accent-gradient);
  color: white;
  border-color: transparent;
}
.kpis-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 0.6rem 0;
}
.soft-card {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 0.9rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  position: relative;
}
.card-title {
  color: #ffffff;
  font-weight: 600;
}
.card-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #ffffff;
}
.card-sub {
  color: #ffffff;
  font-size: 0.85rem;
}
.card-badge {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-weight: 700;
  background: rgba(124, 92, 255, 0.12);
}
.card-badge.up {
  color: #10b981;
}
.card-badge.down {
  color: #ef4444;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-balance {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
  }

  .hero-value {
    font-size: 1.75rem;
  }

  .head-tools {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .chip {
    flex: 1 1 auto;
    min-width: fit-content;
  }

  .kpis-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .card-value {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .hero-balance {
    padding: 1rem;
  }

  .hero-value {
    font-size: 1.5rem;
  }

  .hero-var {
    font-size: 0.9rem;
  }

  .head-tools {
    gap: 0.4rem;
  }

  .chip {
    font-size: 0.8rem;
    padding: 0.35rem 0.5rem;
  }

  .kpis-cards {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .card-value {
    font-size: 1.2rem;
  }

  .card-title {
    font-size: 0.85rem;
  }

  .card-sub {
    font-size: 0.75rem;
  }
}
</style>
